import type {
  QueuedEvent,
  RemoveBoxQueuePayload,
  StatusUpdateRequest,
} from '../types/logistics';

/**
 * Rewrites the temporary box ids in a queued-event list to the real ids the server gave back.
 *
 * A box added while offline gets a negative placeholder id (`use-logistics-api` only treats an
 * id as temporary when `orderBoxId < 0`), and the courier keeps working against it — scanning it
 * to a new status, sometimes removing it again — all before the add has ever reached the server.
 * When the drain finally posts the add and learns the real id, every event still in the queue
 * that points at the placeholder has to be pointed at the real row instead, or it will be sent
 * with an id the backend has never issued.
 *
 * Mutates the events in place and reports whether anything changed, which is what the caller
 * needs in order to decide whether to write the queue back to storage.
 *
 * **Each id is looked up once, against its original value**, so a map that happens to contain a
 * chain does not cascade: `{1 → 2, 2 → 3}` leaves a 1 as a 2. That is deliberate. Real ids are
 * positive and placeholders are negative, so a chain cannot arise from this app — but a rewrite
 * that applied itself repeatedly would be silently destructive if one ever did, and the rule is
 * cheaper to keep than to rediscover.
 */
export function remapQueueIds(queue: QueuedEvent[], idMap: Map<number, number>): boolean {
  if (idMap.size === 0) return false;

  let changed = false;
  queue.forEach((event) => {
    if (event.type === 'STATUS_CHANGE') {
      const payload = event.payload as StatusUpdateRequest;
      const real = idMap.get(payload.orderBoxId);
      if (real !== undefined) {
        payload.orderBoxId = real;
        changed = true;
      }
    } else if (event.type === 'REMOVE_BOX') {
      const payload = event.payload as RemoveBoxQueuePayload;
      const real = idMap.get(payload.orderBoxId);
      if (real !== undefined) {
        payload.orderBoxId = real;
        changed = true;
      }
    }

    if (event.tempBoxId !== undefined && idMap.has(event.tempBoxId)) {
      event.tempBoxId = idMap.get(event.tempBoxId);
      changed = true;
    }
  });

  return changed;
}
